import { useAuth0 } from '@auth0/auth0-react';
import { skipToken } from '@reduxjs/toolkit/query';
import { FeedbackButton } from '@strata/react-buttons';
import { LogoutWarningDialog } from '@strata/react-dialog';
import { BlankScreen } from '@strata/react-page-templates';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { getSupplierFeatures } from 'api/referenceAPI';
import { Header } from 'components/header/Header';
import { clearDataForLogout, getFMHTokenAndEnsureSocketConnections } from 'services/authHelpers';
import { LOGIN_REDIRECT_KEY } from 'services/constants';
import { handleError } from 'services/Error';
import { SupplierFeatures, useSupplierFeatures } from 'services/FeatureFlags';
import { updatePendoUserMetadata } from 'services/pendoUtils';
import { AppRoutes } from 'services/routes';
import { SocketTypes, wsConnect, wsEnsureConnected } from 'services/socketHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetSupplierDetailsQuery } from 'store/api/referenceApi';
import { setSupplierFeatures } from 'store/auth/authSlice';
import { fetchAuthProfile } from 'store/auth/authThunks';
import { setCanLogWebsocketPerformance } from 'store/userSupport/userSupportSlice';
import { registerUser } from 'store/userSupport/userSupportThunks';
import { fetchWorklistToDoTaskCount } from 'store/worklist/worklistThunks';

const TOKEN_REFRESH_THRESHOLD = 5; // minutes

/** Routes that header is hidden on */
const NoHeaderRoutes: string[] = [
    AppRoutes.termsAndConditions,
    AppRoutes.termsOfUse,
    AppRoutes.healthPlanNotes,
    AppRoutes.privacyPolicy,
    AppRoutes.updatesHistory,
];

interface RequireAuthProps {
    requiredSupplierFeature?: string;
}

/**
 * Component to determine if a user is authenticated and
 * if they are allowed to visit the page they navigated to.
 *
 * If they are: they proceed to the page
 * If not: they are redirected to the login page.
 *
 * This component will also refresh the access token if the user is authenticated.
 */
export const RequireAuth = ({ requiredSupplierFeature = undefined }: RequireAuthProps) => {
    const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, logout, isLoading, user } =
        useAuth0();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { pharmacyUserLogoutEnhancement, canLogWebsocketPerformance } = useFlags<{
        pharmacyUserLogoutEnhancement: boolean;
        canLogWebsocketPerformance: boolean;
    }>();
    const [tokenFetchedAt, setTokenFetchedAt] = useState(dayjs());

    const getAccessTokens = useCallback(async () => {
        await getAccessTokenSilently({
            cacheMode: 'off', // Prevents retrieving from cache
        });

        await getFMHTokenAndEnsureSocketConnections({
            getAccessTokenSilently,
            logout,
            dispatch,
        });
        setTokenFetchedAt(dayjs());
    }, [getAccessTokenSilently, logout, dispatch]);

    const wrapGetAccessTokenSilently = useCallback(async () => {
        try {
            // Call Auth0 API to get access token, which will refresh the session and cache the token
            await getAccessTokens();
        } catch (e) {
            // An error in getting the access token invalidates the session and will require logging in again.
            // eslint-disable-next-line no-console
            console.error('Error getting access token silently', e);
        }
    }, [getAccessTokens]);

    // Authentication
    const supplierFeatures = useSupplierFeatures();

    // Ensure npi, permissions, and features are loaded
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    const authProfile = useAppSelector((state) => state.auth.profile);
    const userProfile = useAppSelector((state) => state.userSupport.userProfile);
    const userDataLoaded = !!supplierFeatures;
    useEffect(() => {
        void (async () => {
            if (
                isAuthenticated &&
                !!user &&
                dayjs().diff(tokenFetchedAt, 'minutes') >= TOKEN_REFRESH_THRESHOLD
            ) {
                await wrapGetAccessTokenSilently();
            } else if (!isAuthenticated) {
                sessionStorage.setItem(
                    LOGIN_REDIRECT_KEY,
                    `${location.pathname}${location.search}${location.hash}`,
                );
                await loginWithRedirect();
                // AuthCallback will handle the redirect
            }
        })();
        // Fetch all NPI details if not in store
        if (isAuthenticated && selectedNpi) {
            if (!userDataLoaded) {
                void (async () => {
                    try {
                        const supplierFeaturesResult =
                            (await getSupplierFeatures(selectedNpi))?.map(
                                (feature) => feature.supplier_feature_id,
                            ) ?? [];
                        dispatch(setSupplierFeatures({ supplierFeatures: supplierFeaturesResult }));

                        // Connect to sockets
                        dispatch(wsConnect({ socketType: SocketTypes.AUCTION, npi: selectedNpi }));
                        dispatch(
                            wsConnect({ socketType: SocketTypes.NOTIFICATION, npi: selectedNpi }),
                        );
                        dispatch(
                            wsEnsureConnected({
                                socketType: SocketTypes.USER_SUPPORT,
                                npi: selectedNpi,
                            }),
                        );
                        // Only need performance socket for claims data
                        if (supplierFeaturesResult?.includes(SupplierFeatures.MEDBILLING)) {
                            dispatch(
                                wsConnect({
                                    socketType: SocketTypes.PERFORMANCE,
                                    npi: selectedNpi,
                                }),
                            );
                        }

                        await dispatch(fetchAuthProfile());
                        dispatch(fetchWorklistToDoTaskCount());
                    } catch (err) {
                        handleError('Error loading user data.', err as Error);
                    }
                })();
            }
            if (authProfile && !userProfile) {
                dispatch(
                    registerUser({
                        details: {
                            first_name: authProfile?.firstName,
                            last_name: authProfile?.lastName,
                        },
                    }),
                );
            }
        }
    }, [
        location,
        userDataLoaded,
        isAuthenticated,
        selectedNpi,
        dispatch,
        authProfile,
        userProfile,
        isLoading,
        user,
        wrapGetAccessTokenSilently,
        loginWithRedirect,
        tokenFetchedAt,
    ]);

    // Set Pendo metadata
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        selectedNpi ? { npi: selectedNpi } : skipToken,
    );
    useEffect(() => {
        if (supplierDetails) {
            updatePendoUserMetadata(supplierDetails);
        }
    }, [supplierDetails]);

    useEffect(() => {
        dispatch(setCanLogWebsocketPerformance(canLogWebsocketPerformance));
    }, [dispatch, canLogWebsocketPerformance]);

    if (!isAuthenticated) {
        return <BlankScreen />;
    }

    // Redirect to home if supplier user doesn't have required feature
    if (userDataLoaded && requiredSupplierFeature && !supplierFeatures?.[requiredSupplierFeature]) {
        return <Navigate replace to={AppRoutes.home} />;
    }

    return (
        <>
            {!NoHeaderRoutes.includes(location.pathname) && <Header />}
            {pharmacyUserLogoutEnhancement && (
                <LogoutWarningDialog
                    tokenFetchedAt={tokenFetchedAt.toISOString()}
                    onExtend={getAccessTokens}
                    onLogout={async (isUserInitiated) => {
                        clearDataForLogout(isUserInitiated);
                        await logout({
                            logoutParams: {
                                returnTo: `${window.location.origin}${AppRoutes.loggedOut}`,
                            },
                        });
                    }}
                />
            )}
            {!userDataLoaded ? <BlankScreen /> : <Outlet />}
            <FeedbackButton typeformID="YRPPMZcL" hideButton={!isAuthenticated} />
        </>
    );
};
